/**
 * relation-graph.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { StdVariable } from "../aggregation/typed-parameter";
import { Graph, GraphEdge, GraphNode, SecondaryEdge } from "../graphs/graph";

/**
 * The {@link RelationGraph} is a {@link Graph} of subject relationships.
 */
export class RelationGraph extends Graph<RelationNode, RelationEdge>
{
    /**
     * Relationship edges have labels, but no inherent direction.
     */
    public readonly directed : boolean = false;

    /**
     * Keep the nodes distant enough to fit (most) role names between them.
     */
    public readonly idealLinkLength : number = 180;

    /**
     * A medium link strength (which should be weaker than charge).
     */
    public readonly linkStrength : number = 0.4;

    /**
     * A stronger-than-default charge force, to increase the repellence of each
     * node and encourage untangling of highly-connected graphs.
     */
    public readonly chargeStrength : number = -500;

    /**
     * The edges themselves have types, so we want the graph to stratify them.
     * (By default this will color edges based upon their category property.)
     */
    public readonly categorizeEdges : boolean = true;

    /**
     * The server id of the graph in memory. The server holds onto graphs
     * created by the user (since they contain local references to subject
     * contexts, among other reasons). Thus the ID is necessary to identify
     * which graph is being operated on.
     */
    public objectId : number;

    /**
     * Attempt to load more nodes that are connected to a particular node.
     */
    public expand : (node : RelationNode) => void;
}

/**
 * A {@link RelationEdge} is the graphical equivalent of a {@link Relationship}
 * for a {@link RelationGraph}.
 */
export class RelationEdge extends GraphEdge
{
    /**
     * Create a new {@link RelationEdge} from the nodes it links and the JSON
     * definition. (NOTE: The parameters here implicitly encourage you to use
     * the {@link RelationGraph}.addEdge method rather than direct construction)
     * @param source The source subject node
     * @param target The target subject node
     * @param msg The original edge definition JSON
     */
    constructor (
        source : RelationNode,
        target : RelationNode,
        msg : MessageRelationEdge
    )
    {
        super(source, target);
        this.label = msg.sourceRole;
        this.reverseLabel = msg.targetRole;
        this.category = msg.relationshipType;
    }

    /**
     * Whether the edge represents additional relationships.
     */
    public hasSecondaries : boolean = false;

    /**
     * The additional relationships along this edge.
     */
    public secondaries : SecondaryRelation[] = [];

    /**
     * Add a secondary relationship, if it's not already stored
     * @param msg The JSON definition of the relationship
     */
    public addSecondary (msg : MessageRelationEdge) : void
    {
        this.hasSecondaries = true;
        const existing : SecondaryRelation = this.secondaries.find(
            (s : SecondaryRelation) =>
                (s.label === msg.sourceRole
                    && s.reverseLabel === msg.targetRole));
        if (!existing)
        {
            this.secondaries.push({
                label: msg.sourceRole,
                reverseLabel: msg.targetRole,
                category: msg.relationshipType,
                enabled: true
            });
        }
    }

    protected swapWithSecondary (i : number) : void
    {
        const newSecondary : SecondaryRelation = {
            label: this.label,
            reverseLabel: this.reverseLabel,
            category: this.category,
            enabled: this.enabled
        };
        Object.assign(this, this.secondaries[i]);

        this.secondaries[i] = newSecondary;
    }

    public enableAll () : void
    {
        this.enabled = true;
        this.secondaries.forEach((sr : SecondaryRelation) => sr.enabled = true);
    }

    public filterByRole (roleName : string) : boolean
    {
        if (this.label !== roleName && this.reverseLabel !== roleName)
        {
            this.enabled = false;
        }

        this.secondaries.forEach((sr : SecondaryRelation, i : number) => {
            if (sr.label === roleName
                || sr.reverseLabel === roleName)
            {
                if (sr.enabled && !this.enabled)
                {
                    this.swapWithSecondary(i);
                }
            }
            else
            {
                sr.enabled = false;
            }
        });

        return this.enabled;
    }
    public filterByType (typeName : string) : boolean
    {
        if (this.category !== typeName)
        {
            this.enabled = false;
        }

        this.secondaries.forEach((sr : SecondaryRelation, i : number) => {
            if (sr.category === typeName)
            {
                if (sr.enabled && !this.enabled)
                {
                    this.swapWithSecondary(i);
                }
            }
            else
            {
                sr.enabled = false;
            }
        });

        return this.enabled;
    }
}

/**
 * An additional relationship along an edge. Since most of the data will be
 * duplicates of the host relationship, this merely contains labelling.
 */
export interface SecondaryRelation extends SecondaryEdge
{
    label : string;
    reverseLabel : string;
    category : string;
    enabled : boolean;
}

/**
 * A node of the {@link RelationGraph}, representing a {@link Subject} in the
 * network of displayed relationships.
 */
export class RelationNode extends GraphNode
{
    /**
     * The summary data for the {@link Subject} the node represents
     */
    public summary : StdVariable[];

    /**
     * Whether the node has been expanded; the inverse of whether it may have
     * additional unknown edges.
     */
    public alreadyExpanded : boolean = false;

    /**
     * Create a new {@link RelationNode} from its message content
     * @param mn The JSON node definition
     */
    constructor (mn : MessageRelationNode)
    {
        super(mn.nodeId, mn.summary.contextType);
        this.summary = mn.summary.summary;
    }
}

/**
 * The definition of a relation node, as transmitted by the message protocol
 */
export interface MessageRelationNode
{
  nodeId : number;
  summary : {
      contextType : any;
      summary : StdVariable[];
  };
}

/**
 * The definition of a relation edge, as transmitted by the message protocol
 */
export interface MessageRelationEdge
{
  source : number;
  target : number;
  relationshipType : any; // TODO
  sourceRole : any; // TODO
  targetRole : any; // TODO
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/relation-graph.ts